import React, { useEffect, useState } from "react";
import { Settings, Check, X, Save, Plus, Trash2 } from "lucide-react";
import api from "../lib/api";
import { Card, Button, PageHeader, PageLoader, Badge } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const FLAGS = [
  { key: "station_enabled", label: "Beverage Station" },
  { key: "mother_base_enabled", label: "Mother Base" },
  { key: "espresso_enabled", label: "Espresso Machine" },
  { key: "coffee_counter_enabled", label: "Coffee Machine Counter" },
];

function Toggle({ on, onClick, disabled, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex h-7 w-12 items-center rounded-full px-0.5 transition ${on ? "bg-emerald-500" : "bg-line"} ${disabled ? "opacity-60" : ""}`}>
      <span className={`flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transition ${on ? "translate-x-5" : ""}`}>
        {on ? <Check size={12} className="text-emerald-600" /> : <X size={12} className="text-ink-mute" />}
      </span>
    </button>
  );
}

export default function BranchBeverageSettings() {
  const { t } = useT();
  const toast = useToast();
  const { isRole } = useAuth();
  const canEdit = isRole("OWNER") || isRole("SUPER_ADMIN");
  const [rows, setRows] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = () => api.get("/beverage/branch-settings").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const patch = (bid, key, val) => setRows(rows.map((r) => (r.branch_id === bid ? { ...r, [key]: val } : r)));
  const setLabel = (bid, i, val) => setRows(rows.map((r) => r.branch_id === bid
    ? { ...r, counter_labels: r.counter_labels.map((l, j) => (j === i ? val : l)) } : r));
  const addLabel = (bid) => setRows(rows.map((r) => r.branch_id === bid
    ? { ...r, counter_labels: [...(r.counter_labels || []), ""] } : r));
  const removeLabel = (bid, i) => setRows(rows.map((r) => r.branch_id === bid
    ? { ...r, counter_labels: r.counter_labels.filter((_, j) => j !== i) } : r));

  const save = async (r) => {
    setSavingId(r.branch_id);
    try {
      await api.put(`/beverage/branch-settings/${r.branch_id}`, {
        station_enabled: r.station_enabled, mother_base_enabled: r.mother_base_enabled,
        espresso_enabled: r.espresso_enabled, coffee_counter_enabled: r.coffee_counter_enabled,
        counter_labels: (r.counter_labels || []).filter((l) => l && l.trim()),
      });
      toast.success(t("Settings saved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Failed to save"));
    } finally { setSavingId(null); }
  };

  if (!rows) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="beverage-settings">
      <PageHeader icon={Settings} title={t("Beverage Station Settings")}
        subtitle={canEdit ? t("Configure beverage features per branch") : t("View-only — managed by Owner / Super Admin")} />

      <div className="mt-4 space-y-4">
        {rows.map((r) => (
          <Card key={r.branch_id} className="p-5" data-testid={`bev-branch-${r.branch_id}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-ink">{r.branch_name}</h3>
              {!canEdit && <Badge tone="muted">{t("View only")}</Badge>}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FLAGS.map((f) => (
                <div key={f.key} className="flex items-center justify-between rounded-xl border border-line bg-app px-3 py-2">
                  <span className="text-sm text-ink-soft">{t(f.label)}</span>
                  <Toggle on={!!r[f.key]} disabled={!canEdit}
                    onClick={() => canEdit && patch(r.branch_id, f.key, !r[f.key])}
                    testid={`toggle-${f.key}-${r.branch_id}`} />
                </div>
              ))}
            </div>

            {r.espresso_enabled && (
              <div className="mt-4">
                <p className="mb-1.5 text-xs font-semibold text-ink-soft">{t("Coffee Machine Counter Labels")}</p>
                <div className="space-y-2">
                  {(r.counter_labels || []).map((lbl, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={lbl} disabled={!canEdit}
                        onChange={(e) => setLabel(r.branch_id, i, e.target.value)}
                        data-testid={`counter-label-${r.branch_id}-${i}`}
                        className="flex-1 rounded-xl border border-line bg-app px-3 py-2 text-sm" />
                      {canEdit && (
                        <button type="button" onClick={() => removeLabel(r.branch_id, i)}
                          className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
                      )}
                    </div>
                  ))}
                  {canEdit && (
                    <Button variant="ghost" onClick={() => addLabel(r.branch_id)} className="text-xs"
                      data-testid={`add-counter-label-${r.branch_id}`}>
                      <Plus size={14} /> {t("Add Counter Label")}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {canEdit && (
              <div className="mt-4">
                <Button onClick={() => save(r)} loading={savingId === r.branch_id}
                  data-testid={`save-bev-${r.branch_id}`}>
                  <Save size={14} /> {t("Save")}
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
